import { NgIf } from '@angular/common';
import { Directive, HostListener, Inject, inject, InjectionToken, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import { isNil, toNumber } from 'lodash';

import { SessionStorage } from '@lockton-apps/shared/data-access/storage';
import { CanActivateFn, Router } from '@angular/router';

export interface ExperimentalConfig {
  allowExperimental: boolean;
  experimentalByDefault: boolean;
}

export const EXPERIMENTAL_CONFIG = new InjectionToken<ExperimentalConfig>(
  'Experimental mode config'
);

export const isExperimental = (): boolean =>
  !!toNumber(SessionStorage.get('experimental'));

export function toggleExperimental(value?: boolean): void {
  const sessionValue = value === undefined ? !+isExperimental() : value;
  SessionStorage.set('experimental', toNumber(sessionValue));
  window.location.reload();
}

@Directive({
  selector: '[laIfExperimental]',
  standalone: true,
  hostDirectives: [NgIf],
})
export class IfExperimentalDirective {
  private ngIf = inject(NgIf, { host: true });

  isExperimental = isExperimental();

  constructor() {
    this.ngIf.ngIf = this.isExperimental;
  }
}

@Directive({
  selector: '[laExperimentalManager]',
  standalone: true,
})
export class ExperimentalManagerDirective {
  private isCtrlDown!: boolean;

  @HostListener('document:keyup', ['$event'])
  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    this.isCtrlDown = event.ctrlKey || event.metaKey;

    switch (event.code) {
      case 'KeyE':
        if (!this.isCtrlDown || !this.config.allowExperimental) {
          return;
        }

        toggleExperimental();
        event.preventDefault();
        break;
    }

    this.isCtrlDown = false;
  }

  constructor(
    @Inject(EXPERIMENTAL_CONFIG) private config: ExperimentalConfig
  ) {}
}

function _provideInitExperimental(config: ExperimentalConfig) {
  return [
    {
      provide: EXPERIMENTAL_CONFIG,
      useValue: config,
    },
    provideAppInitializer(() => {
        const initializerFn = (() => {
        return () => {
          if (!config.allowExperimental) {
            return window.sessionStorage.removeItem('experimental');
          }
          const experimental = window.sessionStorage.getItem('experimental');
          if (isNil(experimental) && config.experimentalByDefault) {
            toggleExperimental(true);
          }
        };
      })();
        return initializerFn();
      }),
  ];
}

export function provideInitExperimental(config: ExperimentalConfig) {
  return makeEnvironmentProviders(_provideInitExperimental(config));
}

export const experimentalGuard: CanActivateFn = () => {
  const router = inject(Router);
  return isExperimental() ? true : router.createUrlTree(['/']);
};
